import _ from 'underscore';

import {pluralize} from 'BenchlingUtil';
import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import {TableData} from 'notebook/tables/TableConstants';
import {getSchemaFieldApiIdsInTableSelection} from 'notebook/tables/typed-values/schema/SchemaFieldIdGetters';
import {AssayRun} from 'results/assays/AssayRecordConstants';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import {listToReadable} from 'util/NameUtil';

import {
  ResultFieldValidatableInput,
  ResultValidatableInput,
  RunValidatableInput,
  ValidatableInput,
  ValidatableInputType,
} from './ValidationConstants';

const TYPE_TO_NAME = {
  [ValidatableInputType.RUN]: MODEL_NAMES.run,
  [ValidatableInputType.RESULT]: 'row',
  [ValidatableInputType.RESULT_FIELD]: 'cell',
};

export function validatableInputsForRun(assayRun: AssayRun): Array<RunValidatableInput> {
  return [
    {
      type: ValidatableInputType.RUN,
      assayRunId: assayRun.id,
      assayResultId: null,
      schemaFieldId: null,
    },
  ];
}

export function validatableInputsFromTableSelection(
  table: TableData,
  multiSelectionBox: MultiSelectionBox,
  assayResultIdsByRow: Array<string | null>
): Array<ResultValidatableInput | ResultFieldValidatableInput> {
  const {minI, maxI, minJ, maxJ} = multiSelectionBox;

  const includedAssayResultIds = _.compact(assayResultIdsByRow.slice(minI, maxI + 1));

  const isFullRowSelected = minJ === 0 && maxJ === table.table[0].length - 1;
  if (isFullRowSelected) {
    return includedAssayResultIds.map(
      (assayResultId) =>
        ({
          type: ValidatableInputType.RESULT,
          assayRunId: null,
          assayResultId,
          schemaFieldId: null,
        }) as ResultValidatableInput
    );
  }

  const selectedValidatableSchemaFieldApiIds = getSchemaFieldApiIdsInTableSelection(
    table,
    multiSelectionBox,
    false // Cells for archived fields are not validatable
  );
  // eslint-disable-next-line no-restricted-syntax
  return _.flatten(
    includedAssayResultIds.map((assayResultId) =>
      selectedValidatableSchemaFieldApiIds.map((schemaFieldId) => ({
        type: ValidatableInputType.RESULT_FIELD,
        assayRunId: null,
        assayResultId,
        schemaFieldId,
      }))
    )
  );
}

export function validatableInputsToReadable(
  validatableInputs: Array<ValidatableInput>,
  includeCounts: boolean
): string {
  const typeCounts = _.pairs(_.countBy(validatableInputs, 'type'));
  const readableParts = typeCounts.map(([type, count]: [ValidatableInputType, number]) => {
    const pluralizedName = pluralize(count, TYPE_TO_NAME[type]);
    return includeCounts ? `${count} ${pluralizedName}` : pluralizedName;
  });
  return listToReadable(readableParts);
}
